// Pure helpers for building/ordering the ANALYSIS operation detail row list.
//
// sortAnalysisRows() replaces the previous two-pass sort + O(n^2) findIndex-based
// dedup in OperationsDetail/index.js with a single O(n) merge pass followed by one
// canonical O(n log n) sort. It reproduces the existing row shape/field names exactly
// (isTankFirst, isShowFirstLineData, endOfSrcNameBorder, endOfRepBorder, collection_date,
// user_id, src_name, _originalCollectionDate, _originalUserId, _originalSrcName, items),
// with two intentional behavior fixes agreed with the user:
//   - tank/lot names are sorted naturally (numeric-aware), so "Tank 2" sorts before "Tank 10"
//   - exam ordering uses a single shared Intl.Collator over translated substance labels,
//     instead of repeated getLabel()+localeCompare() calls per comparison
//
// buildAnalysisTimelineViewModel() builds the hierarchical step/source/exam view-model
// consumed by AnalysisTimeline.js (Patch B). Default-expansion and step-state rules
// below reflect two things confirmed against real code/behavior rather than assumed:
//   - sampling is atomic per cycle (one POST covers every source at once), so a
//     historical step's source groups default to collapsed (there is no meaningfully
//     distinguishable "most recently sampled source" to auto-expand)
//   - only the step whose relative_id equals operation.relative_id can ever be
//     editable; a step can be neither completed nor current (e.g. if relative_id and
//     completed+1 diverge) -- that gap is classified 'unknown' and is read-only

/**
 * Conservative numeric normalization: null/undefined/'' -> null, non-finite -> null,
 * otherwise a truncated integer. Never treats booleans as 0/1.
 */
export function toFiniteInteger(value) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : null;
}

/**
 * Classifies a step given the recurring-group metadata. See module header for the
 * reasoning behind the 4-value state (vs. the 6-value state sketched in the design doc).
 */
export function classifyStepState({ relativeId, completedThrough, currentRelativeId, visibleThrough }) {
  if (completedThrough != null && relativeId <= completedThrough) return "completed";
  if (currentRelativeId != null && relativeId === currentRelativeId) return "current";
  if (visibleThrough != null && relativeId > visibleThrough) return "future";
  return "unknown";
}

/**
 * Null-safe range check for a single exam result (Patch T5A). min_range/max_range
 * are independently optional (some substances only define a floor or a ceiling),
 * and a value of 0 must be treated as present -- callers must not use `||` against
 * row.value/min_range/max_range, which would silently drop a valid 0.
 */
export function computeExamRange(row) {
  const rawValue = row?.value;
  const numericValue = Number(rawValue);
  const hasValue =
    rawValue !== null && rawValue !== undefined && rawValue !== "" && Number.isFinite(numericValue);

  const rawMin = row?.min_range;
  const numericMin = Number(rawMin);
  const hasMin =
    rawMin !== null && rawMin !== undefined && rawMin !== "" && Number.isFinite(numericMin);

  const rawMax = row?.max_range;
  const numericMax = Number(rawMax);
  const hasMax =
    rawMax !== null && rawMax !== undefined && rawMax !== "" && Number.isFinite(numericMax);

  const hasRange = hasValue && (hasMin || hasMax);
  const isInside = hasRange
    ? (!hasMin || numericValue >= numericMin) && (!hasMax || numericValue <= numericMax)
    : null;

  return {
    hasRange,
    isInside,
    min: hasMin ? numericMin : null,
    max: hasMax ? numericMax : null,
  };
}

const mergeKey = (an, { isOnTank, separateByRelativeId }) =>
  `${an?.substance_name ?? ""}|${isOnTank ? an?.tank_id : an?.batch_id}|${
    separateByRelativeId ? an?.relative_id : ""
  }`;

const trackerKeyFor = (row, repetitive) => {
  const tankKey = row?.tank_id || row?.batch_id;
  return repetitive ? `${row?.relative_id}_${tankKey}` : tankKey;
};

/**
 * Builds the deduped + canonically ordered row list and the temperature map
 * previously produced inline in OperationsDetail/index.js.
 *
 * @param {Array} analysisArray - operation.treatments + previousTreatments (unsorted)
 * @param {object} options
 * @param {object} options.operation
 * @param {(key: string) => string} options.getLabel
 * @param {Array} options.tanks
 * @param {Array} options.lots
 * @returns {{ rows: Array, temperatureMap: Record<string, any> }}
 */
export function sortAnalysisRows(analysisArray, { operation, getLabel, tanks, lots } = {}) {
  const isOnTank = !!operation?.on_tank;
  const separateByRelativeId = !!operation?.repetitive;

  // Normalize relative_id before anything else (fallback to the current operation's).
  const normalized = (analysisArray || []).map((an) => ({
    ...an,
    relative_id: an?.relative_id || operation?.relative_id,
  }));

  // Preserve the original merge iteration order (relative_id, then tank_id) so that,
  // when duplicate treatment records collide on the same tracker key, the same record
  // "wins" for temperature/collection_date/user_id as before.
  const mergeOrder = [...normalized].sort((a, b) => {
    const relA = Number(a?.relative_id) || 0;
    const relB = Number(b?.relative_id) || 0;
    if (relA !== relB) return relA - relB;
    return (a?.tank_id || 0) - (b?.tank_id || 0);
  });

  const rowsByKey = new Map();
  const rows = [];
  const processedTanks = new Set();
  const temperatureMap = {};

  mergeOrder.forEach((an) => {
    const key = mergeKey(an, { isOnTank, separateByRelativeId });
    const existing = rowsByKey.get(key);

    if (existing) {
      const existingItem = existing.items.find(
        (it) =>
          it?.min_range === an?.min_range &&
          it?.max_range === an?.max_range &&
          it?.unit === an?.unit
      );
      if (existingItem) {
        if (an?.color && !existingItem.colors.includes(an.color)) {
          existingItem.colors.push(an.color);
        }
      } else {
        existing.items.push({
          unit: an?.unit,
          min_range: an?.min_range,
          max_range: an?.max_range,
          colors: an?.color ? [an.color] : [],
        });
      }
      return;
    }

    const source = isOnTank
      ? tanks?.find((t) => t.id == an?.tank_id) // eslint-disable-line eqeqeq
      : lots?.find((l) => l.id == an?.batch_id); // eslint-disable-line eqeqeq

    const tankKey = an?.tank_id || an?.batch_id;
    const trackerKey = separateByRelativeId ? `${an?.relative_id}_${tankKey}` : tankKey;
    const isTankFirst = !processedTanks.has(trackerKey);

    if (isTankFirst && an?.temperature) {
      temperatureMap[trackerKey] = an.temperature;
    }

    const row = {
      ...an,
      batch_name: source?.batch_name,
      _originalCollectionDate: an?.collection_date,
      _originalUserId: an?.user_id,
      _originalSrcName: source?.name,
      temperature: an?.temperature,
      opType: "ANALYSIS",
      items: [
        {
          unit: an?.unit,
          min_range: an?.min_range,
          max_range: an?.max_range,
          colors: an?.color ? [an.color] : [],
        },
      ],
    };

    rowsByKey.set(key, row);
    rows.push(row);
    processedTanks.add(trackerKey);
  });

  // Canonical final order: relative_id ASC -> natural-sort source name -> numeric id
  // tie-break -> exam order (shared collator over translated substance label).
  const translatedLabel = new Map();
  rows.forEach((row) => {
    const name = row?.substance_name;
    if (name != null && !translatedLabel.has(name)) {
      translatedLabel.set(name, getLabel?.(`substance_${name}`) || "");
    }
  });
  const collator = new Intl.Collator(undefined, { sensitivity: "base" });

  rows.sort((a, b) => {
    const relA = Number(a?.relative_id) || 0;
    const relB = Number(b?.relative_id) || 0;
    if (relA !== relB) return relA - relB;

    const nameA = a?._originalSrcName || "";
    const nameB = b?._originalSrcName || "";
    const nameCmp = nameA.localeCompare(nameB, undefined, { numeric: true });
    if (nameCmp !== 0) return nameCmp;

    const idA = Number(a?.tank_id ?? a?.batch_id) || 0;
    const idB = Number(b?.tank_id ?? b?.batch_id) || 0;
    if (idA !== idB) return idA - idB;

    return collator.compare(
      translatedLabel.get(a?.substance_name) || "",
      translatedLabel.get(b?.substance_name) || ""
    );
  });

  // Recompute display flags based on the final (post-sort) order.
  const processedTanksAfterSort = new Set();
  let previousRelId = null;
  rows.forEach((row) => {
    const trackerKeyAfterSort = trackerKeyFor(row, operation?.repetitive);
    const isTankFirstAfterSort = !processedTanksAfterSort.has(trackerKeyAfterSort);

    row.isTankFirst = isTankFirstAfterSort;
    row.isShowFirstLineData = isTankFirstAfterSort;
    row.collection_date = isTankFirstAfterSort ? row?._originalCollectionDate : null;
    row.user_id = isTankFirstAfterSort ? row?._originalUserId : null;
    row.src_name = isTankFirstAfterSort ? row?._originalSrcName : null;
    row.endOfSrcNameBorder = isTankFirstAfterSort;

    const relId = Number(row?.relative_id);
    row.endOfRepBorder = !!operation?.repetitive && relId !== previousRelId;
    previousRelId = relId;

    processedTanksAfterSort.add(trackerKeyAfterSort);
  });

  return { rows, temperatureMap };
}

/**
 * Canonical, order-independent signature of a temperatureMap (output of
 * sortAnalysisRows(...).temperatureMap), used to detect a genuine content change vs.
 * a new-but-equal object reference (OperationsDetail/index.js re-seeds RHF's
 * "temperature" field only when this signature actually changes).
 *
 * @param {Record<string, any>} temperatureMap
 * @returns {string}
 */
export function buildTemperatureSignature(temperatureMap) {
  return Object.entries(temperatureMap || {})
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => `${key}:${value ?? ""}`)
    .join("|");
}

/**
 * Canonical identity lookup used by the Excel import (OperationsDetail/index.js,
 * importExcel): (current step if repetitive) + source (tank_id or batch_id, per
 * operation.on_tank) + substance_id. Matching on tank/batch alone collapses every
 * substance of the same tank onto the same cell, and ignores which cycle a
 * repetitive analysis' rows belong to -- both are needed to identify a unique row.
 *
 * @param {Array} sortedRows - output of sortAnalysisRows(...).rows
 * @param {object} operation
 * @param {object} identity
 * @param {*} identity.tankId
 * @param {*} identity.batchId
 * @param {*} identity.substanceId
 * @returns {object|undefined} the matching row, or undefined if none match
 */
export function findAnalysisRowByIdentity(sortedRows, operation, { tankId, batchId, substanceId }) {
  return (sortedRows || []).find((row) => {
    const matchesStep =
      !operation?.repetitive || Number(row?.relative_id) === Number(operation?.relative_id);
    const matchesSource = operation?.on_tank ? row?.tank_id === tankId : row?.batch_id === batchId;
    return matchesStep && matchesSource && Number(row?.substance_id) === Number(substanceId);
  });
}

/**
 * Pure hierarchical view-model builder (step -> source -> exam), consumed by
 * AnalysisTimeline.js. `sortedRows` must already be in canonical order (i.e. the
 * `rows` returned by sortAnalysisRows) -- this function does not re-sort.
 *
 * `formIndex` mirrors the row's position in `sortedRows` (today's array/RHF index).
 * `formIndexByIdentity` is only built from the current step's exams: it is the only
 * step whose rows are ever registered/editable.
 *
 * @param {Array} sortedRows - output of sortAnalysisRows(...).rows
 * @param {object} operation
 */
export function buildAnalysisTimelineViewModel(sortedRows, operation) {
  const rows = sortedRows || [];

  const maxKnownRelativeId = rows.reduce(
    (max, row) => Math.max(max, Number(row?.relative_id) || 0),
    0
  );
  const totalSteps =
    toFiniteInteger(operation?.total) > 0
      ? toFiniteInteger(operation?.total)
      : maxKnownRelativeId || 1;
  const completedThrough = Math.min(
    Math.max(toFiniteInteger(operation?.completed) ?? 0, 0),
    totalSteps
  );
  const currentRelativeId = toFiniteInteger(operation?.relative_id);
  const isGroupCompleted = completedThrough >= totalSteps;
  const visibleThrough = isGroupCompleted
    ? totalSteps
    : Math.min(Math.max(currentRelativeId ?? 0, completedThrough + 1), totalSteps);
  const hiddenFutureCount = Math.max(totalSteps - visibleThrough, 0);

  const stepsByRelId = new Map();

  rows.forEach((row, formIndex) => {
    const relativeId = Number(row?.relative_id) || 0;
    if (!stepsByRelId.has(relativeId)) {
      const state = classifyStepState({
        relativeId,
        completedThrough,
        currentRelativeId,
        visibleThrough,
      });
      stepsByRelId.set(relativeId, {
        relativeId,
        state,
        isHistorical: state === "completed",
        isCurrent: state === "current",
        isFuture: state === "future",
        isEditable: state === "current",
        isSampled: false,
        hasResults: false,
        sourcesById: new Map(),
        sources: [],
      });
    }
    const step = stepsByRelId.get(relativeId);

    const sourceKind = row?.tank_id != null ? "tank" : "batch";
    const sourceId = row?.tank_id ?? row?.batch_id;
    const sourceMapKey = `${sourceKind}:${sourceId}`;
    if (!step.sourcesById.has(sourceMapKey)) {
      // Canonical order guarantees the first row encountered per source within a step
      // is the same "isTankFirst" row sortAnalysisRows populated collection_date/
      // user_id/temperature on (null on every other row of the same group).
      const source = {
        sourceKind,
        sourceId,
        sourceName: row?._originalSrcName || "",
        // Lot/batch currently held by the tank, surfaced so it stays visible in the
        // source header even while collapsed (Patch T5A). Only meaningful for tank
        // sources -- a batch source already *is* the batch, and sortAnalysisRows only
        // populates row.batch_name by looking up the tank record (see mergeOrder above).
        batchName: sourceKind === "tank" ? row?.batch_name || null : null,
        defaultExpanded: step.isCurrent,
        sampling: {
          sampled: !!row?.collection_date,
          sampledAt: row?.collection_date ?? null,
          sampledByUserId: row?.user_id ?? null,
        },
        exams: [],
        // Finalized in the post-pass below, once every exam of this source is known.
        rangeStatus: "UNKNOWN",
      };
      step.sourcesById.set(sourceMapKey, source);
      step.sources.push(source);
    }
    const source = step.sourcesById.get(sourceMapKey);

    const hasResult = row?.value !== undefined && row?.value !== null && row?.value !== "";
    if (hasResult) step.hasResults = true;
    if (row?.collection_date) step.isSampled = true;

    const range = computeExamRange(row);

    source.exams.push({
      substanceId: row?.substance_id,
      value: row?.value,
      hasResult,
      hasRange: range.hasRange,
      isInside: range.isInside,
      min: range.min,
      max: range.max,
      formIndex,
      key: `${relativeId}|${sourceKind}|${sourceId}|${row?.substance_id}`,
      // Full original row (unit, min/max range, analyst, collection metadata, ...) so
      // consumers can feed it straight into the existing Table/TableCell rendering
      // without re-deriving anything.
      rawReference: row,
    });
  });

  // OUTSIDE if any evaluable exam is out of range, INSIDE if every evaluable exam is
  // in range, UNKNOWN if the source has no exam with an evaluable range at all.
  stepsByRelId.forEach((step) => {
    step.sources.forEach((source) => {
      const evaluableExams = source.exams.filter((exam) => exam.hasRange);
      source.rangeStatus =
        evaluableExams.length === 0
          ? "UNKNOWN"
          : evaluableExams.some((exam) => exam.isInside === false)
            ? "OUTSIDE"
            : "INSIDE";
    });
  });

  const steps = Array.from(stepsByRelId.values())
    .sort((a, b) => a.relativeId - b.relativeId)
    .map(({ sourcesById, ...step }, displayIndex) => ({
      ...step,
      displayIndex,
      // Not-completed group: only the last completed step + the current step start
      // expanded. Completed group: only the very last step starts expanded.
      defaultExpanded: isGroupCompleted
        ? step.relativeId === totalSteps
        : step.isCurrent || (step.isHistorical && step.relativeId === completedThrough),
    }));

  const formIndexByIdentity = new Map();
  const currentStepModel = steps.find((step) => step.isCurrent);
  currentStepModel?.sources.forEach((source) => {
    source.exams.forEach((exam) => {
      formIndexByIdentity.set(exam.key, exam.formIndex);
    });
  });

  const visibleSteps = steps.filter((step) => step.relativeId <= visibleThrough);
  const initialExpandedStepCount = steps.filter((step) => step.defaultExpanded).length;
  const initialRenderedExamCount = currentStepModel
    ? currentStepModel.sources.reduce((sum, source) => sum + source.exams.length, 0)
    : 0;

  return {
    totalSteps,
    completedSteps: completedThrough,
    currentStep: currentRelativeId,
    isGroupCompleted,
    visibleThrough,
    hiddenFutureCount,
    steps,
    formIndexByIdentity,
    metrics: {
      logicalExamCount: rows.length,
      visibleStepCount: visibleSteps.length,
      initialExpandedStepCount,
      initialRenderedExamCount,
    },
  };
}
